<template>
  <v-row dense>
    <v-col>
      <ReportHeading
        label="Пакети до подовження"
        :loading="loading"
        @update-report="updateReport()"
      >
        <template v-slot:action>
          <DownloadFile
            location="xlsx"
            :params="reportData"
            filename="Пакети до подовження"
            extension="xlsx"
            tooltip="Завантажити у форматі Excel"
          />
          <v-switch
            v-model="onlyRecommended"
            :disabled="loading"
            color="green"
            label="Тільки рекомендовані пакети"
            dense
            class="mt-1"
          />
        </template>
        <template v-slot:filters>
          <v-row dense class="pb-3">
            <v-col class="py-0">
              <DatePicker
                v-model="date"
                label="На дату (включно)"
                :filled="false"
                @input="
                  () => {
                    dateMenu = false;
                    updateReport();
                  }
                "
              />
            </v-col>

            <v-col class="py-0">
              <ManagerSelect
                v-model="manager"
                @input="updateReport"
              />
            </v-col>
            <v-col class="py-0">
              <v-autocomplete
                v-model="client"
                :items.sync="clients"
                :disabled="loading"
                :search-input.sync="search"
                clearable
                no-filter
                hide-no-data
                hide-selected
                hide-details
                dense
                label="Ім'я клієнта"
                placeholder="Введіть iм'я клієнта"
                class="mr-1 dense"
                outlined
                return-object
                item-text="title"
                item-value="id"
                append-icon="search"
                @click:append="clientId && (openModal = true)"
              />
              <ModalWindow
                :id="clientId"
                v-model="openModal"
                component-name="Client"
              />
            </v-col>
          </v-row>
        </template>
      </ReportHeading>

      <Totals
        :total-current-balance-hours="totalCurrentBalanceHours"
        :total-recommended-hours="totalRecommendedHours"
        :total-payment-amount="totalPaymentAmount"
      />

      <v-card>
        <SimpleTable
          :headers="headers"
          :loading="loading"
          :items-per-page="filteredItems.length"
          :items="filteredItems"
        />
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import ManagerSelect from "../../catalogs/managers/ManagerSelect.vue";
import DatePicker from "@/components/story/DatePicker.vue";
import Totals from "./Totals.vue";
import DownloadFile from "../../../components/DowloadFile.vue";
import SimpleTable from "@/components/story/SimpleTable";
import * as reportsApi from "../../../backend/reportsApi.js";
import ReportHeading from "../../../components/ReportHeading.vue";
import { columnTypes, numberToString } from "@/helpers";

export default {
  components: {
    ManagerSelect,
    DatePicker,
    Totals,
    DownloadFile,
    SimpleTable,
    ReportHeading,
    ModalWindow() {
      return import("@/components/ModalWindow.vue");
    },
  },
  data: () => ({
    onlyRecommended: false,
    loading: false,
    items: [],
    headers: [
      {
        text: "Клієнт",
        value: "client",
        componentName: "Client",
        reference: true,
        style: "min-width: 100px",
      },
      {
        text: "Попередній Пакет",
        value: "packageArticle",
        type: "text",
      },
      {
        text: "Попередня Дата Сплати",
        value: "prevPaymentDate",
        type: "date",
      },
      {
        text: "Попередня Кількість Годин",
        value: "prevPayedHours",
        type: "text",
      },
      {
        text: "Поточний Залишок",
        value: "currentBalanceHours",
        type: "text",
      },
      {
        text: "Відпрацьовано Годин",
        value: "spentHours",
        type: "text",
      },
      {
        text: "Рекомендована К-сть Годин",
        value: "recommendedHours",
        type: "text",
      },
      {
        text: "Сума до сплати",
        value: "paymentAmount",
        type: "text",
      },
      {
        text: "Рекомендований Пакет",
        value: "recommendedPackage",
        type: "text",
      },
      {
        text: "Сплатити До",
        value: "nextPaymentDate",
        type: "date",
      },
      { text: "Відповідальний Менджер", value: "manager", type: "text" },
      {
        text: "Рахунок",
        value: "lastInvoice",
        reference: true,
        componentName: "Invoice",
        getTitle(cellValue) {
          const { number, date, packageAmount } = cellValue;
          return `№ ${number} від ${date} | ${packageAmount}`;
        },
      },
    ],
    manager: null,
    dateMenu: false,
    date: "",
    client: null,
    openModal: false,
    search: null,
  }),
  computed: {
    filteredItems() {
      let result = this.items;
      if (this.client) {
        result = result.filter((i) => i.client.id === this.client.id);
      }
      if (this.onlyRecommended) {
        result = result.filter((i) => i.recommendedPackage !== null);
      }
      return result;
    },
    clients() {
      let items = this.items.map((i) => i.client);
      if (this.search){
        items = items.filter(i => i.title.toUpperCase().includes(this.search.toUpperCase()));
      }
      return items;
    },
    totalCurrentBalanceHours() {
      return this.items.reduce((sum, i) => sum + i.currentBalanceHours, 0);
    },
    totalRecommendedHours() {
      return this.items.reduce((sum, i) => sum + i.recommendedHours, 0);
    },
    totalPaymentAmount() {
      return this.items.reduce((sum, i) => sum + i.paymentAmount, 0);
    },
    reportData() {
      return {
        columns: this.headers.map(({ text }) => text),
        rows: this.items.map((item) => {
          return [
            item.client.title,
            item.prevPackage?.title || "",
            item.prevPaymentDate,
            numberToString(item.prevPayedHours),
            numberToString(item.currentBalanceHours),
            numberToString(item.spentHours),
            numberToString(item.recommendedHours),
            numberToString(item.paymentAmount),
            item.recommendedPackage?.title || "",
            item.nextPaymentDate,
            item.manager?.title || "",
            item.lastInvoice ? `№ ${item.lastInvoice.number} від ${item.lastInvoice.date} | ${item.lastInvoice.packageAmount}` : "",
          ];
        }),
        columnTypes: [
          columnTypes.STRING,
          columnTypes.STRING,
          columnTypes.DATE,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.NUMBER,
          columnTypes.STRING,
          columnTypes.DATE,
          columnTypes.STRING,
          columnTypes.STRING,
        ],
      };
    },
    clientId(){
      return this.client?.id || null;
    },
  },
  created() {
    this.date = this.moment().subtract(1, "days").format("YYYY-MM-DD");
    this.updateReport();
  },
  methods: {
    async updateReport() {
      this.loading = true;
      this.items = [];

      const managerId = this.manager?.id || "";
      const date = this.moment(this.date).format("YYYY-MM-DD");

      try {
        this.items = await reportsApi
          .packagesUpdateReportOld(managerId, date);
      } catch {
        this.$notifyWarning("В даних звіту помилки. Подивіться в Система/Стан БД");
      }
      this.loading = false;
    },
  },
};
</script>
